import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, of, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { XMLParser } from 'fast-xml-parser';
import { StockDataSourceService } from 'src/app/core/data-sources/stock-data-source.service';
import { ActionService } from 'src/app/core/services/action.service';
import { ErrorsEnum, ErrorService } from 'src/app/core/services/error.service';
import { ProductsInterface } from 'src/app/pages/stock/stock-inventory-sidenav/stock-inventory-sidenav.component';
import { ActionPermissionsService } from 'src/app/core/services/action-permissions.service';
import { UserRoles } from 'src/models/User';
import { ActionsEnum } from 'src/app/core/data-sources/action-data-source.service';

interface TableItem {
  sku: string | number;
  quantity: number;
}

@Component({
  selector: 'app-nfe-in-modal',
  templateUrl: './nfe-in-modal.component.html',
  styleUrls: ['./nfe-in-modal.component.scss']
})
export class NfeInModalComponent implements OnInit {
  private readonly orderNumberPattern = /^[\p{L}\p{N}_-]+$/u;
  displayedColumns = ['sku', 'quantity', 'actions'];
  dataSource: MatTableDataSource<TableItem> = new MatTableDataSource<TableItem>([]);
  products: ProductsInterface[] = [];
  productInputSubscription$: Subscription;
  showTable = false;
  importedFileName: string | null = null;
  userRole: UserRoles = null;
  form: FormGroup = new FormGroup({
    sku: new FormControl(null, Validators.required),
    quantity: new FormControl(null, [Validators.required, Validators.min(1)]),
    description: new FormControl(null),
    orderNumber: new FormControl(null, [Validators.required, Validators.pattern(this.orderNumberPattern)])
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { userRole: UserRoles },
    private stockService: StockDataSourceService,
    public dialogRef: MatDialogRef<NfeInModalComponent>,
    private actionService: ActionService,
    private errorService: ErrorService,
    private actionPermissionsService: ActionPermissionsService,
  ) { }

  ngOnInit(): void {
    this.userRole = this.data?.userRole || null;
    this.productInputSubscription$ = this.form.controls.sku.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((search) => {
        if (typeof search !== 'string' || !search || search.length < 3) {
          this.products = [];
          return of([]);
        }
        return from(this.stockService.getSearchedProducts(search));
      })
    ).subscribe((res: ProductsInterface[]) => {
      this.products = res || [];
    });
  }

  ngOnDestroy(): void {
    this.productInputSubscription$.unsubscribe();
  }

  addManuallyToTable(product) {
    if (this.importedFileName?.length > 0) {
      return;
    }

    const item = {
      sku: product.sku.sku ?? product.sku,
      quantity: product.quantity || 1,
    };

    const current = this.dataSource.data || [];
    this.dataSource.data = [...current, item];
    this.showTable = true;
    this.form.controls.sku.reset();
    this.form.controls.quantity.reset();
    this.form.controls.sku.markAsUntouched();
    this.form.controls.quantity.markAsUntouched();
  }

  async importToTable(products): Promise<void> {
    if (!products || !products.length) {
      return;
    }

    const items = products.map(product => ({ sku: product.sku, quantity: product.quantity }));
    this.dataSource.data = [...this.dataSource.data, ...items];
    this.showTable = true;
    const data = this.dataSource.data || [];
    this.dataSource = new MatTableDataSource(data);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files || !input.files.length) {
      return;
    }

    const file = input.files[0];
    this.importedFileName = file.name;
    this.form.reset();
    this.dataSource.data = [];
    this.showTable = false;
    this.parseXml(file);
  }

  async parseXml(file: File): Promise<void> {
    try {
      const xmlText = await file.text();
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: '@_'
      });
      const data = parser.parse(xmlText);

      const infNFe = data?.nfeProc?.NFe?.infNFe || data?.NFe?.infNFe;
      const detNodes = infNFe?.det;
      const details = Array.isArray(detNodes) ? detNodes : detNodes ? [detNodes] : [];
      const noteNumber = infNFe?.ide?.nNF?.toString?.().trim?.() || infNFe?.['@_Id']?.toString?.().trim?.();

      if (noteNumber) {
        this.form.controls.orderNumber.setValue(noteNumber);
        this.validateImportedOrderNumber();
      }

      const products = details.map((detail) => {
        const product = detail?.prod;
        const sku = product?.cProd?.toString().trim();
        const quantity = Number(product?.qCom);

        if (!sku || quantity <= 0) {
          return null;
        }

        return { sku, quantity };
      }).filter((item) => !!item);

      if (!products.length) {
        this.errorService.openErrorSnackBarWithCustomMessage('Nenhum produto válido foi encontrado nesta Nota Fiscal.');
        return;
      }

      await this.importToTable(products);
    } catch (error) {
      this.errorService.openErrorSnackBarWithCustomMessage('Não foi possível ler o arquivo XML. Verifique se o arquivo é válido.');
    }
  }

  deleteFromTable(index: number) {
    if (!this.dataSource || !this.dataSource.filteredData.length || this.importedFileName.length > 0) {
      return;
    }
    this.dataSource.filteredData.splice(index, 1);
    this.dataSource = new MatTableDataSource(this.dataSource.filteredData);
  }

  buildProductsArray(): { sku: string | number, quantity: number }[] {
    if (!this.dataSource) { 
      return [];
    }

    const rows = (this.dataSource.filteredData && this.dataSource.filteredData.length)
      ? this.dataSource.filteredData
      : (this.dataSource.data || []);
    return rows.map(r => ({ sku: r.sku, quantity: r.quantity || 0 }));
  }

  close() {
    this.form.reset();
    this.showTable = false;
    this.dialogRef.close();
  }

  async submit() {
    const orderNumberControl = this.form.controls.orderNumber;

    if (orderNumberControl.invalid) {
      orderNumberControl.markAsTouched();
      return;
    }

    const products = this.buildProductsArray();

    try {
      const source = this.importedFileName ? 'INVOICE_XML' : 'MANUAL';
      const payload = {
        orderNumber: this.form.value.orderNumber.trim(),
        description: this.form.value.description?.trim() || '',
        entrySource: source,
        products
      };

      await this.actionService.createInternalInOrder(payload);
      this.showTable = false;
      this.dialogRef.close("SUBMITTED");
    } catch (e) {
      const type = e.error?.body?.type;
      const details = e.error?.body?.details;
      switch (type) {
        case ErrorsEnum.IN_ORDER_ERROR_ALREADY_EXISTS:
          this.errorService.openErrorSnackBarWithCustomMessage('Já existe uma ordem de entrada com este número.');
          break;
        case ErrorsEnum.IN_ORDER_ERROR_INVALID_PRODUCT:
          this.errorService.openErrorSnackBarWithCustomMessage(`Os produtos com o seguinte código do item estão duplicados: ${details.sku}`);
          break;
        default:
          this.errorService.openErrorSnackBar(e.error?.body);
      }
    }
  }

  displayedString(product) {
    return product ? (product.name + ' - ' + product.sku) : '';
  }

  private validateImportedOrderNumber(): void {
    const orderNumber = this.form.controls.orderNumber;

    if (orderNumber.hasError('pattern')) {
      orderNumber.markAsTouched();
    }
  }

  canCreateOrder(): boolean {
    return this.actionPermissionsService.hasPermission(ActionsEnum.CREATE_INTERNAL_IN_ORDER, this.userRole);
  }

}
